import React from 'react'
import { QuestionnaireItem } from '@medplum/fhirtypes'
import { useAssessmentContext } from 'contexts/AssessmentContext'
import { getNestedItems } from 'components/Assessments/helpers'
import { DEFAULT_REQUIRED_FIELD_MESSAGE } from 'components/Assessments/constants'
import { useQAAssessmentActionsContext } from 'contexts/QAAssessmentActionsContext'
import { useQAAssessmentCommentsContext } from 'contexts/QAAssessmentCommentsContext'
import { getCommentQuestionLinkId } from 'components/QA/Assessments/helpers'
import { ModifierAFormField } from './formField'
import { ModifierAQuestionnaireItem } from './questionnaire'
import { CodingRadioQuestionWithNotes } from '../../components'
import { PATSectionComponentProps } from '../../types/components'

export default function ModifierASection({
  item: section,
  sectionId: _sectionId,
  sectionIndex: _sectionIndex
}: PATSectionComponentProps): JSX.Element {
  const {
    cantEditAssessmentFields,
    assessmentMustBeFinalized,
    assessmentStatusState: { inQA, inQAEdit }
  } = useAssessmentContext()
  const { createCommentClickHandler, disableHover } = useQAAssessmentActionsContext()
  const { sortedComments: comments, focusedCommentId } = useQAAssessmentCommentsContext()
  const focusedComment = comments.find((c) => c.id === focusedCommentId)
  const focusedCommentLinkId = focusedComment ? getCommentQuestionLinkId(focusedComment) : null

  // Helper function to create comment click handler for a question field
  const createQuestionCommentClickHandler = (field: QuestionnaireItem | undefined) => (): void => {
    if (field?.linkId) {
      createCommentClickHandler(field.linkId)
    }
  }

  // Deterministic mapping of linkId to JSX renderer function.
  const fieldComponentMap: Record<
    string,
    (question: QuestionnaireItem, notes: QuestionnaireItem) => JSX.Element
  > = {
    [ModifierAQuestionnaireItem.MODIFIER_A_1_QUESTION]: (question, notes) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={ModifierAFormField.MODIFIER_A_1_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={ModifierAFormField.MODIFIER_A_1_NOTES}
        disabled={cantEditAssessmentFields}
        customRules={[
          {
            required: Boolean(assessmentMustBeFinalized && question.required),
            message: DEFAULT_REQUIRED_FIELD_MESSAGE
          }
        ]}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
      />
    ),
    [ModifierAQuestionnaireItem.MODIFIER_A_2_QUESTION]: (question, notes) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={ModifierAFormField.MODIFIER_A_2_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={ModifierAFormField.MODIFIER_A_2_NOTES}
        disabled={cantEditAssessmentFields}
        customRules={[
          {
            required: Boolean(assessmentMustBeFinalized && question.required),
            message: DEFAULT_REQUIRED_FIELD_MESSAGE
          }
        ]}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
      />
    ),
    [ModifierAQuestionnaireItem.MODIFIER_A_3_QUESTION]: (question, notes) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={ModifierAFormField.MODIFIER_A_3_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={ModifierAFormField.MODIFIER_A_3_NOTES}
        disabled={cantEditAssessmentFields}
        customRules={[
          {
            required: Boolean(assessmentMustBeFinalized && question.required),
            message: DEFAULT_REQUIRED_FIELD_MESSAGE
          }
        ]}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
      />
    ),
    [ModifierAQuestionnaireItem.MODIFIER_A_4_QUESTION]: (question, notes) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={ModifierAFormField.MODIFIER_A_4_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={ModifierAFormField.MODIFIER_A_4_NOTES}
        disabled={cantEditAssessmentFields}
        customRules={[
          {
            required: Boolean(assessmentMustBeFinalized && question.required),
            message: DEFAULT_REQUIRED_FIELD_MESSAGE
          }
        ]}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
      />
    )
  }

  // Iterate over section.items in questionnaire order
  const questionGroups =
    section?.item && Array.isArray(section.item)
      ? section.item.filter(
          (item): item is QuestionnaireItem => item.type === 'group' && !!item.item
        )
      : []

  // Guard clause: return early if section is undefined, null, or has no items
  if (!section || !section.item || !Array.isArray(section.item) || questionGroups.length === 0) {
    return <></>
  }

  // Render questions in questionnaire order
  return (
    <>
      {questionGroups.map((questionGroup) => {
        const { question, notes } = getNestedItems(questionGroup.item || [])

        if (!question.linkId) {
          return null
        }

        const renderField = fieldComponentMap[question.linkId]

        if (!renderField) {
          return null
        }

        return renderField(question, notes)
      })}
    </>
  )
}
